import { Button, Stack, Typography } from '@mui/material'
import { Controller } from 'react-hook-form'
import RemoveIcon from '@mui/icons-material/Remove'
import AddIcon from '@mui/icons-material/Add'
import { StyledSlider } from '../StyledSlider'
import { InfoBubble } from '~/@ui/info-bubble/InfoBubble'
import {
  getDynamicStep,
  roundToStep,
  sliderPositionToValue,
  valueToSliderPosition,
} from '../_utils/utils'

type Props = {
  suffix: '€' | '% p.a.' | 'mesiacov'
  name: string
  control: any
  min: number
  max: number
  step: number
  disabled?: boolean
  hideMinMax?: boolean
  className?: string
}

export const InvestmenSlider = ({
  suffix,
  name,
  control,
  min,
  max,
  step,
  disabled = false,
  hideMinMax,
  className = '',
}: Props) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => {
        const dynamicStep = getDynamicStep(value, step)
        const sliderValue = valueToSliderPosition(value, min, max)

        return (
          <Stack
            className={`gap-3 px-4 pt-4 pb-1 bg-zBlack300 rounded-4d ${className}`}>
            <Stack className="flex-row gap-0.5">
              <InfoBubble text="Zvoľte si mesačnú sumu, ktorú si môžete pravidelne odkladať pre budúci príjem." />
              <Typography className="text-base font-semibold text-zWhite200">
                Mesačná investícia
              </Typography>
            </Stack>

            <Stack className="flex-row justify-between items-center pt-6">
              <Button
                onClick={() => {
                  const newValue = roundToStep(
                    value - dynamicStep,
                    dynamicStep,
                    min,
                    max
                  )
                  onChange(newValue)
                }}
                disabled={disabled || value <= min}
                className={`w-8 h-8 min-w-[unset] min-h-[unset] p-0 bg-zGrey400 text-zWhite200 ${
                  disabled ? 'cursor-not-allowed' : ''
                }`}>
                <RemoveIcon className="!w-5 !h-5" />
              </Button>
              <Stack className="items-center gap-[1px]">
                <Typography
                  className={`text-6.5d font-black text-zWhite200 tracking-wide`}>
                  {`${value.toLocaleString()} ${suffix}`}
                </Typography>
                {!hideMinMax && (
                  <Stack className="items-center">
                    <Typography
                      className={`text-xs font-semibold text-zGrey400`}>
                      {`Min ${min.toLocaleString('sk-SK')} ${suffix} / Max ${max.toLocaleString()} ${suffix}`}
                    </Typography>
                  </Stack>
                )}
              </Stack>
              <Button
                onClick={() => {
                  const newValue = roundToStep(
                    value + dynamicStep,
                    dynamicStep,
                    min,
                    max
                  )
                  onChange(newValue)
                }}
                disabled={disabled || value >= max}
                className={`w-8 h-8 min-w-[unset] min-h-[unset] p-0 bg-zGrey400 text-zWhite200 ${
                  disabled ? 'cursor-not-allowed' : ''
                }`}>
                <AddIcon className="w-5 h-5" />
              </Button>
            </Stack>

            <Stack className="px-4 pt-1d">
              <StyledSlider
                min={0}
                max={100}
                step={0.1} // Jemný krok pre plynulý pohyb
                value={sliderValue}
                onChange={(_, newSliderValue) => {
                  // Transformujeme slider pozíciu späť na skutočnú hodnotu
                  const realValue = sliderPositionToValue(
                    newSliderValue as number,
                    min,
                    max
                  )
                  // Zaokrúhľujeme na platný krok
                  const currentStep = getDynamicStep(realValue, step)
                  const roundedValue = roundToStep(
                    realValue,
                    currentStep,
                    min,
                    max
                  )
                  onChange(roundedValue)
                }}
                disabled={disabled}
              />
            </Stack>
          </Stack>
        )
      }}
    />
  )
}
